import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function CountUp({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useInView(0.5);
  const started = useRef(false);

  useEffect(() => {
    if (visible && !started.current) {
      started.current = true;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      const interval = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(interval);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
    }
  }, [visible, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function WhoWeAre() {
  const [leftRef, leftVisible] = useInView();
  const [rightRef, rightVisible] = useInView();
  const navigate = useNavigate();


  return (
    <div
      className=" flex items-center justify-center py-20 px-6 md:px-10 lg:px-12 xl:px-20"
      style={{
        background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f0f4f8 100%)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          opacity: 0.3,
        }}
      />

      <div className="relative w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* LEFT — Image */}
          <div
            ref={leftRef}
            className="relative"
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? "translateX(0)" : "translateX(-50px)",
              transition: "opacity 0.8s ease, transform 0.8s cubic-bezier(0.34,1.2,0.64,1)",
            }}
          >
            {/* Main image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80"
                alt="Construction team"
                className="w-full h-[500px] object-cover"
              />
              {/* Overlay gradient */}
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(to bottom, transparent 60%, rgba(10,20,50,0.4) 100%)",
                }}
              />
            </div>

            {/* Years badge */}
            <div
              className="absolute bottom-0 left-0 m-0 px-7 py-5 rounded-tr-2xl rounded-bl-2xl"
              style={{
                background: "linear-gradient(135deg, #0f1f4b, #1a2f6e)",
                minWidth: "200px",
              }}
            >
              <div className="flex items-baseline gap-1">
                <span
                  className="text-white font-black"
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: "clamp(36px, 5vw, 52px)",
                    lineHeight: 1,
                  }}
                >
                  <CountUp target={23} suffix="+" />
                </span>
              </div>
              <p
                className="text-blue-200 text-sm font-medium mt-1 leading-tight"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Years of Industrial<br />Excellence
              </p>
            </div>

            {/* Decorative green corner accent */}
            <div
              className="absolute -top-3 -right-3 w-16 h-16 rounded-xl"
              style={{
                background: "linear-gradient(135deg, #89ea5f, #1fed60)",
                opacity: 0.8,
                zIndex: -1,
              }}
            />
            <div
              className="absolute -bottom-3 -left-3 w-10 h-10 rounded-lg"
              style={{
                background: "linear-gradient(135deg, #0f1f4b, #1a2f6e)",
                opacity: 0.5,
                zIndex: -1,
              }}
            />
          </div>

          {/* RIGHT — Content */}
          <div
            ref={rightRef}
            style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? "translateX(0)" : "translateX(50px)",
              transition: "opacity 0.8s ease 0.2s, transform 0.8s cubic-bezier(0.34,1.2,0.64,1) 0.2s",
            }}
          >
            {/* Label */}
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-6 bg-green-400" />
              <span
                className="text-green-500 font-bold font-rajdhani text-sm tracking-[0.25em] uppercase"
              >
                Who We Are
              </span>
            </div>

            {/* Heading */}
            <h2
              className="font-bold uppercase text-gray-900 mb-5 font-rajdhani text-2xl md:text-4xl lg:text-[45px] tracking-wide leading-[2.5]"
            >
              Engineering Excellence For{" "}
              <span
                style={{
                  WebkitTextStroke: "2px #0f1f4b",
                  color: "transparent",
                }}
              >
                A Smarter
              </span>{" "}
              Future
            </h2>

            {/* Body text */}
            <p className="text-gray-500 text-base leading-relaxed max-w-lg">
             Vishwaraja Enterprises is a dynamic and customer-focused organization committed to delivering quality products and reliable services.
            </p>

            {/* Vision & Mission cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
              {/* Vision */}
              <div
                className="p-5 rounded-xl bg-white shadow-sm border border-gray-100 group hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <h4
                  className="font-black uppercase text-gray-900 mb-3 text-sm md:text-base tracking-wide font-rajdhani leading-7"
                >
                  Our Vision
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Our vision is to become a trusted and leading enterprise recognized for excellence, innovation, and customer satisfaction, while contributing to sustainable growth and creating lasting value for our stakeholders and society.
                </p>
              </div>

              {/* Mission */}
              <div
                className="p-5 rounded-xl bg-white shadow-sm border border-gray-100 group hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <h4
                  className="font-black uppercase text-gray-900 mb-3 text-sm md:text-base tracking-wide font-rajdhani"
                >
                  Our Mission
                </h4>
                 <p className="text-gray-500 text-sm leading-relaxed">
                  Our mission at Vishwaraja Enterprises is to deliver high-quality products and services that meet customer needs with reliability and excellence. We are committed to innovation, ethical business practices, and continuous improvement, while building long-term relationships with our clients and contributing positively to society.
                </p>
              </div>
            </div>

            {/* CTA + Founder */}
            <div className="flex items-center gap-6 flex-wrap">
              {/* Button */}
              <button
              onClick={()=>{
                navigate("/about");
                window.scrollTo({
                  top: 0,
                  behavior : "smooth"
                });
              }}
                className="group relative font-rajdhani overflow-hidden flex items-center gap-3 bg-green-500 text-white font-bold uppercase text-sm px-7 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-green-500/30"
              >
                <span className="relative z-10">Read More</span>
                <span
                  className="relative z-10 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
                <div
                  className="absolute inset-0 bg-gray-900 transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100"
                  style={{ zIndex: 0 }}
                />
              </button>

              {/* Founder */}
                {/* <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full overflow-hidden border-2 border-green-400 flex-shrink-0"
                    style={{ boxShadow: "0 4px 12px rgba(234,88,12,0.3)" }}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80"
                      alt="Marcus Reynolds"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p
                      className="font-black uppercase text-gray-900 text-sm leading-tight"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.05em" }}
                    >
                      Marcus Reynolds
                    </p>
                    <p className="text-gray-400 text-xs uppercase tracking-widest mt-0.5">
                      Founder Steelmind
                    </p>
                  </div>
                </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}